// Onboarding / in-app documentation model (PLAN §12.6).

export const HELP_CATEGORIES = [
  { key: 'getting-started', label: 'Getting started' },
  { key: 'core-concepts', label: 'Core concepts' },
  { key: 'how-to', label: 'How-to guides' },
  { key: 'tips', label: 'Tips' },
  { key: 'faq', label: 'FAQ & troubleshooting' },
  { key: 'admin', label: 'Admin guides' },
  { key: 'whats-new', label: "What's new" },
] as const;
export type HelpCategory = (typeof HELP_CATEGORIES)[number]['key'];

import { can, type Permission, type Role } from './permissions';

export type HelpRole = Role;

export interface HelpArticleMeta {
  slug: string;
  title: string;
  category: HelpCategory;
  /** concept | guide | tip | faq | reference */
  type?: string;
  /** Permission needed to see the article (e.g. editing guides need content.write); omitted = everyone. */
  permission?: Permission;
  tags?: string[];
  related?: string[];
  order?: number;
  summary?: string;
}

/** Viewers don't see editing guides; only admins see admin guides (PLAN §12.6). */
export const helpVisibleTo = (a: Pick<HelpArticleMeta, 'permission' | 'category'>, role: HelpRole): boolean =>
  can(role, a.category === 'admin' ? 'admin.manage' : (a.permission ?? 'content.read'));

/** The article the ? menu opens on each screen. Every route in the app must be listed (tested). */
export const SCREEN_HELP: Record<string, string> = {
  '/': 'getting-started',
  '/upload': 'upload-a-batch',
  '/sheets': 'import-a-profile-sheet',
  '/datasets': 'import-a-dataset',
  '/review': 'review-an-upload',
  '/review/batch': 'review-inbox',
  '/review/unreviewed': 'review-inbox',
  '/directory': 'directory',
  '/entity': 'directory',
  '/entity/new': 'add-and-edit-entities',
  '/library': 'library-and-collections',
  '/cases': 'cases',
  '/graph': 'graph-explorer',
  '/timeline': 'timeline',
  '/map': 'map',
  '/clusters': 'clusters',
  '/flows': 'money-flows',
  '/reports': 'report-document',
  '/reports/new': 'report-document',
  '/findings': 'draft-ai-findings',
  '/chat': 'ask-the-chat',
  '/onboarding': 'getting-started',
  '/admin': 'admin-overview',
  '/admin/users': 'manage-users',
  '/admin/ai': 'ai-routing-and-budgets',
  '/admin/quality': 'compare-ai-routes',
  '/admin/schema': 'schema-and-templates',
  '/admin/templates': 'schema-and-templates',
  '/admin/report-templates': 'templates-for-reports',
  '/admin/lists': 'schema-and-templates',
  '/admin/relations': 'schema-and-templates',
  '/admin/colors': 'color-rules',
  '/admin/search': 'search-index',
  '/admin/matching': 'match-certainty',
  '/admin/audit': 'audit-log',
  '/admin/exports': 'exports-and-watermarks',
  '/admin/backups': 'backups-and-restore',
  '/admin/api-keys': 'ingest-api',
  '/admin/scrapers': 'website-monitors',
  '/admin/registers': 'public-registers',
  '/admin/social': 'social-feeds',
  '/admin/sources': 'source-dashboards',
  '/admin/outlets': 'source-registry',
  '/admin/monitoring': 'monitoring-and-alerts',
  '/admin/erasures': 'erase-data',
  '/admin/help': 'write-help-articles',
  '/activity': 'progress-and-activity',
  '/design': 'colors-and-shapes',
  '/login': 'getting-started',
  '/request-access': 'getting-started',
  '/set-password': 'signing-in',
};

/**
 * Articles linked from the app before their feature exists, with the task that writes them.
 * The help-coverage check (task 27a) requires this list to be empty at launch.
 */
export const PLANNED_ARTICLES: Record<string, string> = {};

/**
 * Domain terms every user meets (PLAN §2 and the screens). The help-coverage check requires a glossary entry
 * (docs/help/glossary.yaml) for each; add the term here when a feature introduces one.
 */
export const GLOSSARY_TERMS = [
  'Actor',
  'Allegation',
  'Assessment',
  'Asset',
  'Attribution level',
  'Authenticator',
  'Auto-match',
  'Backup codes',
  'Batch review',
  'Camp',
  'Case',
  'Cited source',
  'Claim',
  'Cluster',
  'Collection',
  'Color by',
  'Color rule',
  'Compare mode',
  'Confidential',
  'Disputed',
  'Entity',
  'Event',
  'Evidence quote',
  'Extraction depth',
  'Fact',
  'Findings',
  'Flow',
  'Fuzzy date',
  'Gazetteer',
  'ID code',
  'Identity confidence',
  'Investigation status',
  'Library',
  'Marking question',
  'Mentioned in',
  'Merge',
  'OCR',
  'Participation',
  'Place',
  'Pre-selected match',
  'Profile',
  'Profile template',
  'Provenance',
  'Rating dimension',
  'Reading guide',
  'Rejected',
  'Relation',
  'Reliability grade',
  'Report',
  'Report template',
  'Review Inbox',
  'Routing mode',
  'Same person?',
  'Sensitivity',
  'Social feed',
  'Source channel',
  'Source',
  'Speaker mapping',
  'Standard',
  'Statement',
  'Superseded',
  'Tag',
  'Transcript',
  'Unidentified actor',
  'Unmerge',
  'Unreviewed',
  'Verified',
  'Website monitor',
] as const;

// Custom articles (PLAN §12.6): admins write program-specific articles in the app; they're shown and searched
// alongside the built-in ones. Built-in articles win a slug clash (a later release may add one with the same slug).

export const CUSTOM_ARTICLE_CATEGORIES = HELP_CATEGORIES.filter(c => c.key !== 'whats-new');
export const CUSTOM_ARTICLE_LIMITS = { title: 120, summary: 300, body: 50_000, tags: 20, tag: 40, related: 10 } as const;
/** Reserved: the glossary page lives at /onboarding/glossary. */
const RESERVED_SLUGS = new Set(['glossary', 'new']);
const SLUG_RE = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
export const isHelpSlug = (s: unknown): s is string => typeof s === 'string' && s.length >= 3 && s.length <= 80 && SLUG_RE.test(s);

/** "Our research methodology" → "our-research-methodology" (Latin letters and digits; accents dropped). */
export const helpSlugify = (title: string) =>
  title
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 80)
    .replace(/-+$/, '');

export interface CustomArticleInput {
  slug: string;
  title: string;
  category: HelpCategory;
  type?: string | null;
  permission?: Permission | null;
  tags: string[];
  related: string[];
  summary?: string | null;
  body: string;
}

export interface CustomArticle extends CustomArticleInput {
  id: string;
  updatedAt: number;
  updatedByName?: string | null;
}

const PERMISSION_KEYS = ['content.read', 'content.write', 'content.review', 'content.export', 'admin.manage'] as const;

/** Validates and normalizes an article from the editor; errors are plain-language, one per problem. */
export function validateCustomArticle(raw: unknown): { ok: true; value: CustomArticleInput } | { ok: false; errors: string[] } {
  const r = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
  const errors: string[] = [];
  const str = (v: unknown) => (typeof v === 'string' ? v.trim() : '');
  const list = (v: unknown) =>
    Array.isArray(v) ? [...new Set(v.filter((x): x is string => typeof x === 'string').map(x => x.trim()))].filter(Boolean) : [];
  const title = str(r.title);
  const slug = str(r.slug) || helpSlugify(title);
  const body = typeof r.body === 'string' ? r.body.replace(/\r\n/g, '\n').trim() : '';
  const summary = str(r.summary) || null;
  const type = str(r.type) || null;
  const category = str(r.category) as HelpCategory;
  const permission = (str(r.permission) || null) as Permission | null;
  const tags = [...new Set(list(r.tags).map(t => t.toLowerCase()))];
  const related = list(r.related);
  const L = CUSTOM_ARTICLE_LIMITS;

  if (!title) errors.push('Give the article a title.');
  else if (title.length > L.title) errors.push(`Keep the title under ${L.title} characters.`);
  if (!isHelpSlug(slug)) errors.push('The address must be 3–80 lowercase letters, digits and dashes (e.g. our-methodology).');
  else if (RESERVED_SLUGS.has(slug)) errors.push(`“${slug}” is reserved; choose another address.`);
  if (!CUSTOM_ARTICLE_CATEGORIES.some(c => c.key === category)) errors.push('Choose a section for the article.');
  if (permission && !PERMISSION_KEYS.includes(permission as (typeof PERMISSION_KEYS)[number]))
    errors.push('Choose who can read the article.');
  if (!body) errors.push('Write the article text.');
  else if (body.length > L.body) errors.push(`The article is too long (over ${L.body.toLocaleString('en')} characters); split it in two.`);
  if (summary && summary.length > L.summary) errors.push(`Keep the summary under ${L.summary} characters.`);
  if (tags.length > L.tags || tags.some(t => t.length > L.tag)) errors.push(`Use at most ${L.tags} tags of up to ${L.tag} characters.`);
  if (related.length > L.related || related.some(s => !isHelpSlug(s) && s !== 'glossary'))
    errors.push(`Related articles must be up to ${L.related} article addresses.`);
  if (errors.length) return { ok: false, errors };
  return { ok: true, value: { slug, title, category, type, permission, tags, related, summary, body } };
}

/** Getting-started checklist (PLAN §12.6). */
export const CHECKLIST = [
  {
    key: 'authenticator',
    label: 'Set up the authenticator app',
    href: '/onboarding/getting-started#1-sign-in-and-set-up-your-authenticator',
  },
  { key: 'tour', label: 'Take the tour', href: '/' },
  { key: 'core-concepts', label: 'Read "Core concepts"', href: '/onboarding/core-concepts' },
  { key: 'first-upload', label: 'Upload your first document', href: '/upload' },
  { key: 'first-review', label: 'Review it', href: '/review' },
  { key: 'explore-graph', label: 'Explore the graph', href: '/graph' },
] as const;
export type ChecklistKey = (typeof CHECKLIST)[number]['key'];
export const isChecklistKey = (k: string): k is ChecklistKey => CHECKLIST.some(c => c.key === k);
